import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Upload,
  Image,
  Video,
  FileText,
  MessageSquare,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  X,
  Trash2,
  AlertTriangle,
  Camera,
  Inbox,
  Plus,
  Lightbulb,
  Sparkles,
  Bug,
  Eye,
  ArrowRightCircle,
  XCircle,
  Archive,
  Package,
  Layers,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { StaggerList, StaggerItem } from '../../components/animations/StaggerList';
import { WORK_ITEM_STATUSES } from '@yoda/shared';
import { api } from '../../lib/api';
import toast from 'react-hot-toast';

interface Project {
  id: string;
  name: string;
  description: string | null;
  iconUrl: string | null;
  coverImageUrl: string | null;
  organizationId: string;
  organizationName: string;
  status: string;
  workItemCount: number;
  pendingApprovalCount: number;
}

interface WorkItem {
  id: string;
  title: string;
  description: string | null;
  type: string;
  status: string;
  fileUrl: string | null;
  thumbnailUrl: string | null;
  commentCount: number;
}

interface RequestItem {
  id: string;
  title: string;
  type: 'idea' | 'feature' | 'bug';
  status: string;
  createdAt: string;
  commentCount: number;
}

interface Bundle {
  id: string;
  title: string;
  status: string;
  workItemCount: number;
  createdAt: string;
}

const requestTypeConfig = {
  idea: { icon: Lightbulb, label: 'Idea', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  feature: { icon: Sparkles, label: 'Feature', color: 'text-violet-500', bg: 'bg-violet-500/10' },
  bug: { icon: Bug, label: 'Bug', color: 'text-red-500', bg: 'bg-red-500/10' },
};

const requestStatusConfig: Record<string, { icon: typeof Inbox; label: string; color: string; bg: string }> = {
  new: { icon: Inbox, label: 'New', color: 'text-blue-500', bg: 'bg-blue-500/10' },
  under_review: { icon: Eye, label: 'Under Review', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  plan_proposed: { icon: FileText, label: 'Plan Proposed', color: 'text-violet-500', bg: 'bg-violet-500/10' },
  approved: { icon: CheckCircle2, label: 'Approved', color: 'text-green-500', bg: 'bg-green-500/10' },
  converted: { icon: ArrowRightCircle, label: 'Converted', color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
  declined: { icon: XCircle, label: 'Declined', color: 'text-red-500', bg: 'bg-red-500/10' },
  archived: { icon: Archive, label: 'Archived', color: 'text-gray-500', bg: 'bg-gray-500/10' },
};

const statusIcons: Record<string, typeof Clock> = {
  draft: FileText,
  seeking_commentary: MessageSquare,
  approval_requested: Clock,
  changes_requested: AlertCircle,
  approved: CheckCircle2,
};

export function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [requests, setRequests] = useState<RequestItem[]>([]);
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'requests' | 'work-items'>('requests');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingIcon, setIsUploadingIcon] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const [projectRes, workItemsRes, requestsRes, bundlesRes] = await Promise.all([
          api.get<Project>(`/projects/${id}`),
          api.get<WorkItem[]>(`/projects/${id}/work-items${statusFilter ? `?status=${statusFilter}` : ''}`),
          api.get<RequestItem[]>(`/requests?projectId=${id}`) as any,
          api.get<Bundle[]>(`/bundles?projectId=${id}`) as any,
        ]);
        setProject(projectRes.data);
        setWorkItems(Array.isArray(workItemsRes.data) ? workItemsRes.data : []);
        setRequests(requestsRes.data || []);
        setBundles(bundlesRes.data || []);
        setError(null);
      } catch (err) {
        setError('Failed to load project');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    if (id) fetchData();
  }, [id, statusFilter]);

  const getStatusBadgeVariant = (status: string) => {
    const variants: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'info' | 'accent'> = {
      draft: 'default',
      seeking_commentary: 'warning',
      approval_requested: 'accent',
      changes_requested: 'danger',
      approved: 'success',
    };
    return variants[status] || 'default';
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setSelectedFiles(Array.from(files));
      // Auto-fill title from first file name
      if (files.length > 0 && !uploadTitle) {
        const name = files[0].name.replace(/\.[^/.]+$/, ''); // Remove extension
        setUploadTitle(name);
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files) {
      setSelectedFiles(Array.from(files));
      if (files.length > 0 && !uploadTitle) {
        const name = files[0].name.replace(/\.[^/.]+$/, '');
        setUploadTitle(name);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const removeFile = (index: number) => {
    setSelectedFiles(files => files.filter((_, i) => i !== index));
  };

  const resetUploadModal = () => {
    setSelectedFiles([]);
    setUploadTitle('');
    setUploadDescription('');
    setUploadModalOpen(false);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !uploadTitle.trim()) {
      toast.error('Please select a file and enter a title');
      return;
    }

    setIsUploading(true);
    try {
      const file = selectedFiles[0];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', uploadTitle.trim());
      formData.append('projectId', id!);

      // Determine file type
      let fileType = 'document';
      if (file.type.startsWith('image/')) fileType = 'image';
      else if (file.type.startsWith('video/')) fileType = 'video';
      formData.append('type', fileType);
      if (uploadDescription.trim()) {
        formData.append('description', uploadDescription.trim());
      }

      await api.upload(`/projects/${id}/work-items`, formData);

      toast.success('File uploaded successfully!');
      resetUploadModal();

      // Refresh work items
      const workItemsRes = await api.get<WorkItem[]>(`/projects/${id}/work-items`);
      setWorkItems(Array.isArray(workItemsRes.data) ? workItemsRes.data : []);
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return Image;
    if (file.type.startsWith('video/')) return Video;
    return FileText;
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setIsUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.upload(`/uploads/project-cover/${id}`, formData);
      const data = response.data as { coverImage: string };

      setProject((prev) => prev ? { ...prev, coverImageUrl: data.coverImage } : null);
      toast.success('Cover image updated successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload cover image');
    } finally {
      setIsUploadingCover(false);
      if (coverInputRef.current) {
        coverInputRef.current.value = '';
      }
    }
  };

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setIsUploadingIcon(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.upload(`/uploads/project-icon/${id}`, formData);
      const data = response.data as { iconUrl: string };

      setProject((prev) => prev ? { ...prev, iconUrl: data.iconUrl } : null);
      toast.success('Project icon updated successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload project icon');
    } finally {
      setIsUploadingIcon(false);
      if (iconInputRef.current) {
        iconInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== project?.name) {
      toast.error('Please type the project name to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      await api.delete(`/projects/${id}`);
      toast.success('Project deleted successfully');
      navigate('/projects');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete project');
    } finally {
      setIsDeleting(false);
    }
  };

  const resetDeleteModal = () => {
    setDeleteConfirmText('');
    setDeleteModalOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error || 'Project not found'}</p>
        <Link to="/projects">
          <Button variant="secondary">Back to Projects</Button>
        </Link>
      </div>
    );
  }

  // Count items by status
  const statusCounts = workItems.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-6">
      {/* Hidden file inputs for cover and icon */}
      <input
        ref={coverInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleCoverUpload}
      />
      <input
        ref={iconInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleIconUpload}
      />

      {/* Cover Image */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative -mx-4 lg:-mx-6 -mt-8"
      >
        <button
          onClick={() => coverInputRef.current?.click()}
          disabled={isUploadingCover}
          className="relative w-full h-48 bg-bg-tertiary overflow-hidden group"
        >
          {project.coverImageUrl ? (
            <img
              src={project.coverImageUrl}
              alt={project.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent/20 to-accent-light/10">
              <Camera className="w-12 h-12 text-text-muted" />
            </div>
          )}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            {isUploadingCover ? (
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            ) : (
              <div className="text-center">
                <Camera className="w-8 h-8 text-white mx-auto mb-2" />
                <span className="text-white text-sm font-medium">Change Cover Image</span>
              </div>
            )}
          </div>
        </button>
      </motion.div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex gap-4">
            {/* Project Icon */}
            <button
              onClick={() => iconInputRef.current?.click()}
              disabled={isUploadingIcon}
              className="relative w-16 h-16 rounded-xl bg-bg-tertiary overflow-hidden group flex-shrink-0 border-2 border-border hover:border-accent/50 transition-colors"
            >
              {project.iconUrl ? (
                <img
                  src={project.iconUrl}
                  alt={project.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent/20 to-accent-light/10">
                  <span className="text-2xl font-bold text-accent">{project.name.charAt(0)}</span>
                </div>
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                {isUploadingIcon ? (
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <Camera className="w-5 h-5 text-white" />
                )}
              </div>
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold">{project.name}</h1>
                <Badge variant={project.status === 'active' ? 'success' : 'default'}>
                  {project.status}
                </Badge>
              </div>
              <p className="text-text-secondary mt-1">{project.organizationName}</p>
              {project.description && (
                <p className="text-text-muted mt-2">{project.description}</p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Link to={`/requests/new?projectId=${id}`}>
              <Button variant="secondary">
                <Plus className="w-4 h-4" />
                New Request
              </Button>
            </Link>
            <Button onClick={() => setUploadModalOpen(true)}>
              <Upload className="w-4 h-4" />
              Upload Work Item
            </Button>
            <Button
              variant="secondary"
              onClick={() => setDeleteModalOpen(true)}
              className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="border-b border-border"
      >
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('requests')}
            className={`pb-3 px-1 font-medium transition-colors relative ${
              activeTab === 'requests'
                ? 'text-accent'
                : 'text-text-secondary hover:text-text'
            }`}
          >
            <div className="flex items-center gap-2">
              <Inbox className="w-4 h-4" />
              Requests
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-bg-tertiary">
                {requests.length}
              </span>
            </div>
            {activeTab === 'requests' && (
              <motion.div
                layoutId="activeProjectTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent"
              />
            )}
          </button>
          <button
            onClick={() => setActiveTab('work-items')}
            className={`pb-3 px-1 font-medium transition-colors relative ${
              activeTab === 'work-items'
                ? 'text-accent'
                : 'text-text-secondary hover:text-text'
            }`}
          >
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4" />
              Work
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-bg-tertiary">
                {workItems.length + bundles.length}
              </span>
            </div>
            {activeTab === 'work-items' && (
              <motion.div
                layoutId="activeProjectTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent"
              />
            )}
          </button>
        </div>
      </motion.div>

      {/* Status filters for Work Items */}
      {activeTab === 'work-items' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap gap-2"
        >
          <Button
            variant={statusFilter === null ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter(null)}
          >
            All ({workItems.length})
          </Button>
          {Object.entries(WORK_ITEM_STATUSES).map(([key, status]) => {
            const count = statusCounts[key] || 0;
            if (count === 0) return null;
            return (
              <Button
                key={key}
                variant={statusFilter === key ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setStatusFilter(key)}
              >
                <span
                  className="w-2 h-2 rounded-full mr-1.5"
                  style={{ backgroundColor: status.color }}
                />
                {status.label} ({count})
              </Button>
            );
          })}
        </motion.div>
      )}

      {/* Requests List */}
      {activeTab === 'requests' && (
        <>
          {requests.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <Inbox className="w-12 h-12 mx-auto text-text-muted mb-4" />
              <h3 className="text-lg font-semibold mb-2">No requests yet</h3>
              <p className="text-text-secondary mb-4">
                Create your first request for this project
              </p>
              <Link to={`/requests/new?projectId=${id}`}>
                <Button>
                  <Plus className="w-4 h-4" />
                  New Request
                </Button>
              </Link>
            </motion.div>
          ) : (
            <StaggerList className="space-y-2">
              {requests.map((request) => {
                const typeConfig = requestTypeConfig[request.type];
                const statusConf = requestStatusConfig[request.status];
                const TypeIcon = typeConfig?.icon || Lightbulb;
                const StatusIcon = statusConf?.icon || Inbox;

                return (
                  <StaggerItem key={request.id}>
                    <Link to={`/requests/${request.id}`}>
                      <motion.div
                        whileHover={{ x: 4 }}
                        className="p-4 bg-bg-secondary rounded-xl border border-border hover:border-accent/30 transition-all"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${typeConfig?.bg || 'bg-gray-500/10'}`}>
                            <TypeIcon className={`w-5 h-5 ${typeConfig?.color || 'text-gray-500'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium truncate">{request.title}</h3>
                            <p className="text-sm text-text-secondary">
                              {new Date(request.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConf?.bg || 'bg-gray-500/10'} ${statusConf?.color || 'text-gray-500'}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConf?.label || request.status}
                          </div>
                          {request.commentCount > 0 && (
                            <span className="flex items-center gap-1 text-xs text-text-muted">
                              <MessageSquare className="w-3 h-3" />
                              {request.commentCount}
                            </span>
                          )}
                        </div>
                      </motion.div>
                    </Link>
                  </StaggerItem>
                );
              })}
            </StaggerList>
          )}
        </>
      )}

      {/* Work Tab Content */}
      {activeTab === 'work-items' && (
        <div className="space-y-8">
          {/* Bundles Section */}
          {bundles.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-accent" />
                <h2 className="text-lg font-semibold">Bundles</h2>
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-bg-tertiary text-text-secondary">
                  {bundles.length}
                </span>
              </div>
              <StaggerList className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {bundles.map((bundle) => (
                  <StaggerItem key={bundle.id}>
                    <Link to={`/bundles/${bundle.id}`}>
                      <motion.div whileHover={{ y: -4 }} className="cursor-pointer">
                        <Card className="overflow-hidden group" glow>
                          <div className="relative aspect-video bg-gradient-to-br from-accent/20 to-accent-light/10 flex items-center justify-center">
                            <Package className="w-16 h-16 text-accent/50" />
                            <div className="absolute top-2 left-2">
                              <div className="p-1.5 rounded-lg bg-black/50 backdrop-blur-sm">
                                <Package className="w-4 h-4 text-white" />
                              </div>
                            </div>
                          </div>
                          <CardContent className="p-3">
                            <h3 className="font-medium truncate group-hover:text-accent transition-colors">
                              {bundle.title}
                            </h3>
                            <div className="flex items-center justify-between mt-2">
                              <Badge
                                variant={bundle.status === 'approved' ? 'success' : bundle.status === 'ready_for_review' ? 'warning' : 'default'}
                                size="sm"
                              >
                                {bundle.status.replace(/_/g, ' ')}
                              </Badge>
                              <span className="flex items-center gap-1 text-xs text-text-muted">
                                <Layers className="w-3 h-3" />
                                {bundle.workItemCount} items
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Link>
                  </StaggerItem>
                ))}
              </StaggerList>
            </div>
          )}

          {/* Work Items Section */}
          <div>
            {(bundles.length > 0 || workItems.length > 0) && (
              <div className="flex items-center gap-2 mb-4">
                <Image className="w-5 h-5 text-accent" />
                <h2 className="text-lg font-semibold">Work Items</h2>
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-bg-tertiary text-text-secondary">
                  {workItems.length}
                </span>
              </div>
            )}
            {workItems.length === 0 && bundles.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <Image className="w-12 h-12 mx-auto text-text-muted mb-4" />
                <h3 className="text-lg font-semibold mb-2">No work items yet</h3>
                <p className="text-text-secondary mb-4">
                  Upload your first design, video, or document
                </p>
                <Button onClick={() => setUploadModalOpen(true)}>
                  <Upload className="w-4 h-4" />
                  Upload Work Item
                </Button>
              </motion.div>
            ) : workItems.length === 0 ? (
              <p className="text-text-muted text-center py-4">No standalone work items</p>
            ) : (
        <StaggerList className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {workItems.map((item) => {
            const StatusIcon = statusIcons[item.status] || FileText;
            const statusConfig = WORK_ITEM_STATUSES[item.status as keyof typeof WORK_ITEM_STATUSES];

            return (
              <StaggerItem key={item.id}>
                <Link to={`/work-items/${item.id}`}>
                  <motion.div
                    whileHover={{ y: -4 }}
                    className="cursor-pointer"
                  >
                    <Card className="overflow-hidden group" glow>
                    {/* Thumbnail */}
                    <div className="relative aspect-video bg-bg-tertiary overflow-hidden">
                      {item.thumbnailUrl ? (
                        <motion.img
                          src={item.thumbnailUrl}
                          alt={item.title}
                          className="w-full h-full object-cover"
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.3 }}
                        />
                      ) : item.type === 'image' && item.fileUrl ? (
                        <motion.img
                          src={item.fileUrl}
                          alt={item.title}
                          className="w-full h-full object-cover"
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.3 }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {item.type === 'video' ? (
                            <Video className="w-12 h-12 text-text-muted" />
                          ) : (
                            <FileText className="w-12 h-12 text-text-muted" />
                          )}
                        </div>
                      )}

                      {/* Type indicator */}
                      <div className="absolute top-2 left-2">
                        <div className="p-1.5 rounded-lg bg-black/50 backdrop-blur-sm">
                          {item.type === 'image' && <Image className="w-4 h-4 text-white" />}
                          {item.type === 'video' && <Video className="w-4 h-4 text-white" />}
                          {item.type === 'document' && <FileText className="w-4 h-4 text-white" />}
                        </div>
                      </div>

                      {/* Actions overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>

                    {/* Content */}
                    <CardContent className="p-3">
                      <h3 className="font-medium truncate group-hover:text-accent transition-colors">
                        {item.title}
                      </h3>

                      <div className="flex items-center justify-between mt-2">
                        <Badge variant={getStatusBadgeVariant(item.status)} size="sm">
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig?.label || item.status}
                        </Badge>

                        {item.commentCount > 0 && (
                          <span className="flex items-center gap-1 text-xs text-text-muted">
                            <MessageSquare className="w-3 h-3" />
                            {item.commentCount}
                          </span>
                        )}
                      </div>
                    </CardContent>
                    </Card>
                  </motion.div>
                </Link>
              </StaggerItem>
            );
          })}
          </StaggerList>
            )}
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <Modal
        isOpen={uploadModalOpen}
        onClose={resetUploadModal}
        title="Upload Work Item"
        description="Drag and drop files or click to browse"
        size="lg"
      >
        <div className="space-y-4">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,video/*,.pdf,.doc,.docx"
            onChange={handleFileSelect}
          />

          {/* Drop zone */}
          <motion.div
            whileHover={{ scale: 1.01 }}
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-border rounded-2xl p-12 text-center cursor-pointer hover:border-accent transition-colors"
          >
            <Upload className="w-12 h-12 mx-auto text-text-muted mb-4" />
            <p className="text-lg font-medium">Drop files here or click to browse</p>
            <p className="text-sm text-text-muted mt-1">
              Images, videos, PDFs up to 100MB
            </p>
          </motion.div>

          {/* Selected files */}
          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-text-secondary">Selected files:</p>
              {selectedFiles.map((file, index) => {
                const FileIcon = getFileIcon(file);
                return (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 rounded-xl bg-bg-tertiary"
                  >
                    <FileIcon className="w-5 h-5 text-accent" />
                    <span className="flex-1 truncate text-sm">{file.name}</span>
                    <span className="text-xs text-text-muted">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1 hover:bg-bg-secondary rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-text-muted hover:text-red-500" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Title and description inputs */}
          {selectedFiles.length > 0 && (
            <div className="space-y-4">
              <Input
                label="Title"
                placeholder="Enter a title for this work item"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                required
              />
              <div>
                <label className="block text-sm font-medium mb-2">Description (optional)</label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl bg-bg-tertiary border border-border focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 resize-none"
                  placeholder="Add a description for this work item..."
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={resetUploadModal}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={selectedFiles.length === 0 || !uploadTitle.trim() || isUploading}
              isLoading={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Upload Files'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={resetDeleteModal}
        title="Delete Project"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-500">This action cannot be undone</p>
              <p className="text-sm text-text-secondary mt-1">
                Deleting this project will permanently remove all work items, comments, and files.
              </p>
            </div>
          </div>

          <div>
            <p className="text-sm text-text-secondary mb-2">
              To confirm, type <span className="font-semibold text-text">{project.name}</span> below:
            </p>
            <Input
              placeholder={project.name}
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={resetDeleteModal}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleteConfirmText !== project.name || isDeleting}
              isLoading={isDeleting}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              <Trash2 className="w-4 h-4" />
              Delete Project
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
